import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import Tooltip from '../../components/Tooltip';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import {
  PostLinkAccess,
  PostType,
  UserType,
} from '../../graphql/generated/graphql';
import compareIcon from '../../icons/non-prefilled/16x16/compare.svg';
import copyIcon from '../../icons/non-prefilled/16x16/copy.svg';
import memberIcon from '../../icons/non-prefilled/16x16/member.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import sourceIcon from '../../icons/non-prefilled/16x16/source.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import getExternalBrandLogoProps from '../../integrations/getExternalBrandLogoProps';
import { externalViewText } from '../../utils/post/externalPost';
import { createPostPath } from '../../utils/url/postPath';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import { isShareDropdownOpenAtom } from '../postView/isShareDropdownOpenAtom';
import usePublishPost from '../usePublishPost';
import useToggleArchived from '../useToggleArchived';
import * as styles from './PostStateBarActions.module.scss';
import SVGIcon from '../../components/SVGIcon';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';

const POST_FRAGMENT = gql(`
  fragment PostStateBarActions_post on Post {
    id
    archivedAt
    publishedAt
    type
    externalPost {
      id
      platform
      readUrl
    }
    ...usePostPolicyCheck_post
    ...useToggleArchived_post
  }
`);

interface PostStateBarActionsProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  isPublic: boolean;
}

const PostStateBarActions = ({ post, isPublic }: PostStateBarActionsProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const currentUser = useAppSelector((state) => state.session.user);
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);
  const setIsShareDropdownOpen = useSetAtom(isShareDropdownOpenAtom);
  const toggleArchived = useToggleArchived();
  const publishPost = usePublishPost(post.id);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const isPostPublishDisabled =
    currentUser.type === UserType.Guest && post.topics.length === 0;

  type Action = {
    name: string;
    onClick?: () => unknown;
    to?: string;
    icon: React.ReactNode;
    isPrimary?: boolean;
    isDisabled?: boolean;
    tooltip?: string;
  };

  const actions: Action[] = [
    !!(!!post.archivedAt && canEdit) && {
      name: 'Unarchive',
      icon: unarchiveIcon,
      onClick: () => toggleArchived(post),
    },
    !!(!post.publishedAt && !post.archivedAt && canEdit) && {
      name: 'Publish',
      icon: publishIcon,
      onClick: () => publishPost(true),
      isPrimary:
        post.linkAccess !== PostLinkAccess.Disabled &&
        post.linkAccess !== PostLinkAccess.Public &&
        post.linkAccess !== PostLinkAccess.PublicEdit,
      isDisabled: isPostPublishDisabled,
      tooltip: isPostPublishDisabled
        ? 'Guests must publish\nposts in at least\none topic'
        : undefined,
    },
    !!(!post.archivedAt && post.type === PostType.Template) && {
      name: 'Create post',
      icon: copyIcon,
      isPrimary: true,
      to: createPostPath({ templateId: post.id }),
    },
    !!(post.externalPost && !post.archivedAt) && {
      name: externalViewText(post.externalPost.platform),
      icon: (
        <ExternalBrandLogo
          {...getExternalBrandLogoProps(post.externalPost.platform)}
        />
      ),
      to: post.externalPost.readUrl,
    },
    !!(!post.archivedAt && post.type === PostType.Variant) && {
      name: 'View source',
      icon: sourceIcon,
    },
    !!(!post.archivedAt && post.type === PostType.Variant) && {
      name: 'Compare with source',
      icon: compareIcon,
    },
    !!(!post.archivedAt && isPublic && canEdit) && {
      name: 'Edit sharing',
      icon: memberIcon,
      onClick: () => setIsShareDropdownOpen({ open: true, autoFocus: false }),
    },
  ].filter((item) => !!item);

  const primaryAction = actions.find((action) => action.isPrimary);
  const secondaryActions = actions.filter((action) => action !== primaryAction);

  return (
    <div className={styles.container}>
      {secondaryActions.length > 0 && (
        <OverflowMenu
          open={isOverflowMenuOpen}
          onOpenChange={setIsOverflowMenuOpen}
          button={
            <Button
              className={classNames(styles.overflowMenuButton, {
                [styles.isOpen]: isOverflowMenuOpen,
              })}
              active={isOverflowMenuOpen}
              aria-label="Options"
            >
              <SVGIcon>{overflowMenuIcon}</SVGIcon>
            </Button>
          }
        >
          {secondaryActions.map((action) => (
            <Tooltip
              key={action.name}
              placement="bottom"
              content={action.tooltip}
            >
              <MenuItem
                icon={action.icon}
                onClick={action.onClick}
                to={action.to}
                disabled={action.isDisabled}
              >
                {action.name}
              </MenuItem>
            </Tooltip>
          ))}
        </OverflowMenu>
      )}
      {primaryAction && (
        <Tooltip placement="bottom" content={primaryAction.tooltip}>
          <Button
            variant="primary"
            to={primaryAction.to}
            onClick={primaryAction.onClick}
            size="small"
            disabled={primaryAction.isDisabled}
          >
            {primaryAction?.name}
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default PostStateBarActions;
